import IConversationRenderer from "@/components/conversation-renderer/conversation-renderer";
import ConversationRenderer from "@/components/conversation-renderer/conversation-renderer.vue";
import IConversationTestWidget from "@/components/conversation-test-widget/conversation-test-widget";
import ConversationTestWidget from "@/components/conversation-test-widget/conversation-test-widget.vue";
import ConversationVoiceGenerationTimer from "@/components/conversation-voice-generation-timer/conversation-voice-generation-timer.vue";
import QuickViewWindow from "@/components/quick-view-window/quick-view-window.vue";
import { ConversationBuilderLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { VoiceGenStatus } from "@/services/conversation-builder";
import { CLUValidations, PathIntentDuplicateUtterances } from "@/util/constants";
import { BuilderPageType, InteractionStyles, QuickViewWindowType } from "@/util/enum";
import { hasMaxLimitOfUtterancesExceeded } from "@/util/func";
import { Paths } from "@/util/paths";
import { Badge, Button, Modal } from "ant-design-vue";
import { ConversationPublishStatus, fmtMsg, GLUtil } from "commonui";
import { differenceBy, isNull, isUndefined, uniq } from "lodash";
import moment from "moment";
import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import ConversationActions from "./components/conversation-actions/conversation-actions.vue";
import ConversationBreadcrumb from "./components/conversation-breadcrumb/conversation-breadcrumb.vue";
import ConversationMaster from "./components/conversation-master/conversation-master.vue";
import ConversationProperties from "./components/conversation-properties/conversation-properties.vue";
import { invalidIntentNameRegex } from "./components/intent-add-dialog";
import { countObjects, EntityLengths, isUtterenceValueValid, validateUtterances } from "./conversation-builder-util";
import {
	Connector,
	ConversationNode,
	ConversationNodeType,
	Dialog,
	NodeData,
	Path
} from "./conversation-builder.model";
import { Entity } from "./models/entity.model";
import { Intent } from "./models/intent.model";

interface ConversationUnderPublish {
	conversationId: number;
	isTestConversation: boolean;
}

@Component({
	components: {
		ConversationProperties,
		ConversationBreadcrumb,
		ConversationActions,
		QuickViewWindow,
		ConversationRenderer,
		Button,
		ConversationMaster,
		Badge,
		ConversationTestWidget,
		ConversationVoiceGenerationTimer
	}
})
export default class ConversationBuilder extends Vue {
	private duplicatedValidationResolveCallback!: Function;
	private duplicatedValidationRejectCallback!: Function;
	public ConversationNodeType = ConversationNodeType;
	public quickViewType: QuickViewWindowType = QuickViewWindowType.Content;
	public quickViewId: string | null = null;
	private conversationStore = getModule(ConversationBuilderState);
	private shownPathLimitMsg = false;
	private testUXClicked = false;
	private testBtnClicked = false;
	public isValidatingMetadataForTest = false;
	public isValidatingMetadataForTestUX = false;
	public isValidatingMetadataForCreateOrTrain = false;
	public validateNodesWorker: Worker | undefined;

	public $refs!: {
		renderer: IConversationRenderer;
		testWidget: IConversationTestWidget;
	};

	//private service: ICoversationBuilderService | null = null;

	async beforeRouteLeave(to, from, next) {
		// check if any image is being uploaded
		// wait max for 2 minutes
		const startTime = moment();
		if (this.conversationStore.fileUploads.length && moment(startTime).diff(moment(), "m") < 2) {
			await new Promise((res) => setTimeout(res, 5000));
		}
		// save conversation state
		await this.conversationStore.saveConversationState({
			id: this.$route.params.id,
			type: this.$route.params.pagetype as BuilderPageType
		});
		next();
	}

	created() {
		if (this.$route.params.pagetype === BuilderPageType.ContentInfo) {
			this.quickViewType = QuickViewWindowType.Content;
		} else if (this.$route.params.pagetype === BuilderPageType.ExposureInfo) {
			this.quickViewType = QuickViewWindowType.Exposure;
		}
		this.quickViewId = this.$route.params.id;

		// when user tries to close the window
		window.onbeforeunload = () => {
			this.conversationStore.saveConversationState({
				id: this.$route.params.id,
				type: this.$route.params.pagetype as BuilderPageType
			});
			if (this.conversationStore.fileUploads.length) {
				return fmtMsg(ConversationBuilderLocale.ConversationPendingChangesMsg);
			}
			return null;
		};

		this.validateNodesWorker = new Worker("@/util/workers/validatenodesworker.ts", { type: "module" });

		(this.validateNodesWorker as Worker).onmessage = (event) => {
			if (event.data.type == "success") {
				this.duplicatedValidationResolveCallback(event.data.result);
			} else {
				this.duplicatedValidationRejectCallback();
			}
		};
	}

	mounted() {
		// subscribe to change events (an example)
		// this.service = new CoversationBuilderService();
		// this.service?.subscribe();

		// save conversation state every n seconds
		setInterval(
			() =>
				this.conversationStore.saveConversationState({
					id: this.$route.params.id,
					type: this.$route.params.pagetype as BuilderPageType
				}),
			5 * 1000
		);
	}

	beforeDestroy() {
		//this.service?.unSubscribe();
		window.onbeforeunload = null;
		this.validateNodesWorker?.terminate();
	}

	@Watch("conversationStore.currentPublishStatus", { immediate: true })
	showMessage() {
		const resetState = () => {
			this.conversationStore.setTrainingStatus(ConversationPublishStatus.None);
		};
		const { currentPublishStatus } = this.conversationStore;
		if (
			currentPublishStatus === ConversationPublishStatus.Initial ||
			currentPublishStatus === ConversationPublishStatus.Created ||
			currentPublishStatus === ConversationPublishStatus.Trained
		) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishingMsg));
		} else if (currentPublishStatus === ConversationPublishStatus.Deployed) {
			this.conversationStore.getConversationVoiceGenerationStatus();
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishedMsg));
		} else if (currentPublishStatus === ConversationPublishStatus.Failed) {
			this.$message.warning(fmtMsg(ConversationBuilderLocale.ConversationPublishFailedMsg));
			resetState();
		}
	}

	@Watch("conversationStore.currentTestPublishStatus", { immediate: true })
	async showTestMessage() {
		const resetState = (status = ConversationPublishStatus.None) => {
			this.conversationStore.setTestTrainingStatus(status);
		};
		const { currentTestPublishStatus: currentPublishStatus } = this.conversationStore;
		if (
			currentPublishStatus === ConversationPublishStatus.Initial ||
			currentPublishStatus === ConversationPublishStatus.Created ||
			currentPublishStatus === ConversationPublishStatus.Trained
		) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishingTestMsg));
		} else if (currentPublishStatus === ConversationPublishStatus.Deployed) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishedTestMsg));
			await this.conversationStore.getTestVoiceGenerationStatus();
			resetState();
			if (this.testUXClicked) {
				this.testUXClicked = false;
				// Show message that voice URLs are being generated.
				this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishingTestVoiceMsg));
			} else if (this.testBtnClicked) {
				this.testBtnClicked = false;
				if (!this.isVoiceGenerationUnderOrPublishProcess()) {
					this.$refs.testWidget.testConversation();
				}
			}
		} else if (currentPublishStatus === ConversationPublishStatus.Failed) {
			this.$message.warning(fmtMsg(ConversationBuilderLocale.ConversationPublishFailedTestMsg));
			resetState();
		}
	}

	@Watch("conversationStore.testVoiceGenStatus")
	showVoiceGenStatus() {
		const resetState = (status: VoiceGenStatus = VoiceGenStatus.None) => {
			this.conversationStore.setTestVoiceGenerationStatus(status);
		};
		const { testVoiceGenStatus: voiceGenStatus } = this.conversationStore;
		if (voiceGenStatus === VoiceGenStatus.Queued) {
			//this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishingTestVoiceMsg));
			resetState(VoiceGenStatus.waitingForNextStatus);
		} else if (voiceGenStatus === VoiceGenStatus.Running) {
			//this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishingTestVoiceMsg));
			resetState(VoiceGenStatus.waitingForNextStatus);
		} else if (voiceGenStatus === VoiceGenStatus.Success) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishedTestVoiceMsg));
			resetState();
		} else if (voiceGenStatus === VoiceGenStatus.Error || voiceGenStatus === VoiceGenStatus.NoVoiceError) {
			this.$message.warning(fmtMsg(ConversationBuilderLocale.ConversationPublishFailedTestVoiceMsg));
			resetState();
		}
	}

	@Watch("conversationStore.conversationVoiceGenStatus")
	showConversationVoiceGenStatus() {
		const resetState = (status: VoiceGenStatus = VoiceGenStatus.None) => {
			this.conversationStore.setConversationVoiceGenerationStatus(status);
		};
		const { conversationVoiceGenStatus: voiceGenStatus } = this.conversationStore;
		if (voiceGenStatus === VoiceGenStatus.Queued) {
			resetState(VoiceGenStatus.waitingForNextStatus);
		} else if (voiceGenStatus === VoiceGenStatus.Running) {
			resetState(VoiceGenStatus.waitingForNextStatus);
		} else if (voiceGenStatus === VoiceGenStatus.Success) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishedVoiceMsg));
			resetState();
		} else if (voiceGenStatus === VoiceGenStatus.Error || voiceGenStatus === VoiceGenStatus.NoVoiceError) {
			this.$message.warning(fmtMsg(ConversationBuilderLocale.ConversationPublishFailedVoiceMsg));
			resetState();
		}
	}

	@Watch("conversationStore.masterTemplateId")
	updateRoute() {
		if (this.conversationStore.masterTemplateId && !this.$route.params.id) {
			this.$router.replace(
				GLUtil.pathStringify(Paths.MasterConversationBuilder, {
					id: this.conversationStore.masterTemplateId,
					pagetype: BuilderPageType.MasterInfo
				})
			);
		}
	}

	public get isPublished() {
		const publishStatusId = this.conversationStore.currentPublishStatus;
		if (
			!!this.conversationStore.conversationExtraInfo.cluAccountId ||
			publishStatusId === ConversationPublishStatus.Deployed
		) {
			return true;
		}
		return false;
	}

	public get canTestConversation() {
		return this.conversationStore.conversationTestExtraInfo.conversationId;
	}

	public get canTestUXConversation() {
		return (
			this.conversationStore.conversationTestExtraInfo.conversationId && this.conversationStore.isUXTestingAllowed
		);
	}

	public get conversationId() {
		return this.conversationStore.conversationExtraInfo.conversationId
			? this.conversationStore.conversationExtraInfo.conversationId
			: 0;
	}

	public get testConversationId() {
		return this.conversationStore.conversationTestExtraInfo.conversationId
			? this.conversationStore.conversationTestExtraInfo.conversationId
			: 0;
	}

	public get isMasterConversation(): boolean {
		return this.$route.params.pagetype === BuilderPageType.MasterInfo;
	}

	public get disableCreateOrPublish() {
		const publishStatusId = this.conversationStore.currentPublishStatus;
		if (
			!Object.keys(this.conversationStore.nodes).length ||
			this.conversationStore.createOrPublisingConversation ||
			publishStatusId == ConversationPublishStatus.Initial ||
			publishStatusId == ConversationPublishStatus.Created ||
			publishStatusId == ConversationPublishStatus.Trained
		) {
			return true;
		}
		return false;
	}

	public get disableTestButton() {
		return (
			this.conversationStore.createOrPublisingTestConversation ||
			this.conversationStore.currentTestPublishStatus === ConversationPublishStatus.Initial ||
			this.conversationStore.currentTestPublishStatus === ConversationPublishStatus.Created ||
			this.conversationStore.currentTestPublishStatus === ConversationPublishStatus.Trained ||
			!Object.keys(this.conversationStore.nodes).length
		);
	}

	public get disableTestUXButton() {
		return (
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.Running ||
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.Queued ||
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.waitingForNextStatus ||
			this.disableTestButton
		);
	}

	public get isVoiceGenerationUnderOrPublish() {
		if (
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.Running ||
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.Queued ||
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.waitingForNextStatus ||
			this.conversationStore.conversationVoiceGenStatus == VoiceGenStatus.Running ||
			this.conversationStore.conversationVoiceGenStatus == VoiceGenStatus.Queued ||
			this.conversationStore.conversationVoiceGenStatus == VoiceGenStatus.waitingForNextStatus
		) {
			return true;
		} else {
			return false;
		}
	}

	public isVoiceGenerationUnderOrPublishProcess() {
		if (this.isVoiceGenerationUnderOrPublish || this.disableCreateOrPublish || this.disableTestButton) {
			this.$message.warning(fmtMsg(ConversationBuilderLocale.ConversationVoiceGenerationOrPublishMsg));
			return true;
		} else {
			return false;
		}
	}

	public get conversationUnderProcess(): ConversationUnderPublish {
		let conversationId = 0;
		let isTestConversation = false;

		if (
			this.conversationStore.conversationVoiceGenStatus == VoiceGenStatus.Running ||
			this.conversationStore.conversationVoiceGenStatus == VoiceGenStatus.Queued ||
			this.conversationStore.conversationVoiceGenStatus == VoiceGenStatus.waitingForNextStatus
		) {
			conversationId = this.conversationId;
			isTestConversation = false;
		} else if (
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.Running ||
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.Queued ||
			this.conversationStore.testVoiceGenStatus == VoiceGenStatus.waitingForNextStatus
		) {
			conversationId = this.testConversationId;
			isTestConversation = true;
		}
		return { conversationId: conversationId, isTestConversation: isTestConversation };
	}

	public get showCreateTrain() {
		return this.$route.params.pagtype !== BuilderPageType.MasterInfo;
	}

	public addNode<T = NodeData>(node: ConversationNode<T>): void {
		if (node.type === ConversationNodeType.Path && !this.shownPathLimitMsg && !this.validateNodeCount()) {
			this.shownPathLimitMsg = true;
		}
		this.conversationStore.addNode<T>(node);
		this.$forceUpdate();
	}

	public addNodeConnector(nodeId: string, targetNodeId: string, connectorId: string): void {
		const message = this.connectorPostValidation(nodeId, targetNodeId);
		if (message) {
			this.$message.warning(message);
			return;
		}
		this.conversationStore.addConnector({
			sourceId: nodeId,
			targetId: targetNodeId,
			connectorId
		});
	}

	public addNodeConnectorBetweenNodes(nodeId: string, targetNodeId: string, connectorId: string): void {
		this.conversationStore.addConnector({
			sourceId: nodeId,
			targetId: targetNodeId,
			connectorId
		});
	}

	private getConnectors(nodeId: string, source = true): ConversationNode<Connector>[] {
		const connectors: ConversationNode<Connector>[] = [];
		const dataKey = source ? "sourceId" : "targetId";
		for (const key in this.conversationStore.nodes) {
			const currentNode = this.conversationStore.nodes[key] as ConversationNode<Connector>;
			if (currentNode.type === ConversationNodeType.Connector && currentNode.data[dataKey] === nodeId) {
				connectors.push(currentNode);
			}
		}
		return connectors;
	}

	public connectorPreValidation(nodeId: string): string | null {
		const node = this.conversationStore.nodes[nodeId];

		const connectors = this.getConnectors(node.id);

		// 1) Path Validation
		if (node.type === ConversationNodeType.Path) {
			// 1.1) Should only have one connector
			if (connectors.length) {
				return fmtMsg(ConversationBuilderLocale.ConversationPathMultipleConnectMsg);
			}
		}

		// 2) Extenal Conversation Validation
		if (node.type === ConversationNodeType.ExternalConversation) {
			return fmtMsg(ConversationBuilderLocale.ConversationExternalNoConnectMsg);
		}

		// 3) Dialog validation
		if (node.type === ConversationNodeType.Dialog && connectors.length) {
			// 3.1) If Dialog is already connected with another dialog, then cannot add a connector
			const connectorWithTarget = connectors.find((f) => f.data.targetId); // finds a previous fully connected connector
			if (connectorWithTarget) {
				const prevTarget = this.conversationStore.nodes[connectorWithTarget.data.targetId!]; // gets the target node
				if (prevTarget.type === ConversationNodeType.Dialog) {
					return fmtMsg(ConversationBuilderLocale.ConversationDialogNoMultipleMsg);
				}
			}
		}

		return null;
	}

	public connectorPostValidation(nodeId: string, targetNodeId: string): string | null {
		const sourceNode = this.conversationStore.nodes[nodeId];
		const targetNode = this.conversationStore.nodes[targetNodeId];
		const connectors: ConversationNode<Connector>[] = [];
		for (const key in this.conversationStore.nodes) {
			const currentNode = this.conversationStore.nodes[key] as ConversationNode<Connector>;
			if (currentNode.type === ConversationNodeType.Connector) {
				connectors.push(currentNode);
			}
		}

		// if there is already a connection, don't add another connector
		if (connectors.some((s) => s.data.sourceId === sourceNode.id && s.data.targetId == targetNode.id)) {
			return fmtMsg(ConversationBuilderLocale.ConversationTargetSourceAlreadyConnected);
		}

		// 1) Dialog Validation
		if (sourceNode.type === ConversationNodeType.Dialog || sourceNode.type === ConversationNodeType.Conversation) {
			// 2.1 Target should be an path or dialog
			if (!(targetNode.type === ConversationNodeType.Path || targetNode.type === ConversationNodeType.Dialog)) {
				return fmtMsg(ConversationBuilderLocale.ConversationTargetPathDialogMsg);
			}

			// 2.2 Targated path shouldn't already be connected to another node.
			// if (targetNode.type === ConversationNodeType.Path) {
			// 	const connectors = this.getConnectors(targetNode.id, false);
			// 	if (connectors.length) {
			// 		return fmtMsg(ConversationBuilderLocale.ConversationPathConnectDialogMsg);
			// 	}
			// }

			// 2.3 Target should be a Path or Dialog
			const sourceConnectors = this.getConnectors(sourceNode.id);
			const anyTarget = sourceConnectors.find((f) => f.data.targetId);
			const prevTarget = anyTarget ? this.conversationStore.nodes[anyTarget.data.targetId!].type : null;
			if (prevTarget && prevTarget !== targetNode.type) {
				return fmtMsg(ConversationBuilderLocale.ConversationTargetSameTypeMsg);
			}

			// 2.4 if there was a previous connection with dialog, then cannot connect with any other node
			if (prevTarget === ConversationNodeType.Dialog) {
				return fmtMsg(ConversationBuilderLocale.ConversationDialogNoMoreChild);
			}
		}

		// 2) Path Validation
		if (sourceNode.type === ConversationNodeType.Path) {
			// 3.1) Target should be a dialog or external conversation
			if (
				targetNode.type !== ConversationNodeType.Dialog &&
				targetNode.type !== ConversationNodeType.ExternalConversation
			) {
				return fmtMsg(ConversationBuilderLocale.ConversationTargetDialog);
			}
		}

		// All validation passed
		return null;
	}

	// return true for valid addition
	private validateNodeCount(): boolean {
		// validate the max number of entities and intents
		const { nodes, intents, entities } = this.conversationStore;
		const { entityCount, intentCount } = countObjects(nodes, entities, intents);
		const { intent: maxIntents, entities: maxEntities } = EntityLengths.conversation.child;
		const isInvalid = intentCount >= maxIntents || entityCount >= maxEntities;
		if (isInvalid) {
			const node = intentCount >= maxIntents ? "Intent" : "Entity";
			const maxcount = intentCount >= maxIntents ? maxIntents : maxEntities;
			this.$message.warning(
				fmtMsg(ConversationBuilderLocale.ConversationMaxNodeCountWarning, { node, maxcount })
			);
		}
		return !isInvalid;
	}

	public async createOrTrain() {
		if (this.isVoiceGenerationUnderOrPublishProcess()) {
			return;
		}
		this.isValidatingMetadataForCreateOrTrain = true;
		if (!(await this.validateBeforePublish())) {
			this.isValidatingMetadataForCreateOrTrain = false;
			return;
		}
		this.isValidatingMetadataForCreateOrTrain = false;
		this.purifyData();
		this.conversationStore.CreateOrTrainConversation({
			id: this.$route.params.id,
			pagetype: this.$route.params.pagetype as BuilderPageType,
			isTrained: this.isPublished
		});
	}

	private setTestAndTestUxLoadingStatus(testUxClicked: boolean, value: boolean) {
		if (testUxClicked) {
			this.isValidatingMetadataForTestUX = value;
		} else {
			this.isValidatingMetadataForTest = value;
		}
	}

	public async testConversation(testUXClicked: boolean = false) {
		if (this.isVoiceGenerationUnderOrPublishProcess()) {
			return;
		}

		this.setTestAndTestUxLoadingStatus(testUXClicked, true);
		// validate main conversation metaDto
		if (!(await this.validateBeforePublish(false))) {
			this.setTestAndTestUxLoadingStatus(testUXClicked, false);
			return;
		}
		this.setTestAndTestUxLoadingStatus(testUXClicked, false);

		const { conversationId } = this.conversationStore.conversationTestExtraInfo;

		// if there are no changes in test conversation, then allow testing without publishing.
		if (conversationId && conversationId > 0 && !this.conversationStore.testConversationChanges.length) {
			if (testUXClicked) {
				this.gotToTestUXStudentSite();
			} else {
				this.$refs.testWidget.testConversation();
			}
			return;
		}
		this.testUXClicked = testUXClicked;
		this.testBtnClicked = !testUXClicked;
		// purifyData
		this.purifyData();
		// publish test conversation
		this.conversationStore.CreateOrTrainTestConversation({
			id: this.$route.params.id,
			pagetype: this.$route.params.pagetype as BuilderPageType
		});
	}

	public async gotToTestUXStudentSite() {
		// Commented as per GL-13101
		// const speed = this.conversationStore.validSpeeds[this.conversationStore.currentSpeed]
		// 	.replace("x", "")
		// 	.replace(".", "-");
		const studentSitePath = `testconversation/conversation/${this.conversationStore.conversationTestExtraInfo.conversationId}`;
		window.open(process.env.VUE_APP_STUDENT_PORTAL_URL + studentSitePath, "_blank")?.focus();
	}

	private async validateBeforePublish(checkNameUniqueness = true): Promise<boolean> {
		const { nodes, conversationProperties, entities, intents } = this.conversationStore;
		const validationDuration = 20; // in seconds

		const showWarning = (msg: string) => {
			this.$message.warning(msg, validationDuration);
		};

		const isExistingPath = Object.keys(nodes).some((n) => nodes[n].type === ConversationNodeType.Path);
		if (!isExistingPath) {
			showWarning(fmtMsg(ConversationBuilderLocale.ConversationPathRequiredMsg));
			return false;
		}

		// Check intent and entity count (Dialog limit removed (GL-9561))
		const isValidCount = this.validateNodeCount();
		if (!isValidCount) {
			return false;
		}

		// check the conversation properties validations
		if (
			!CLUValidations.conversationNameRegex.test(conversationProperties.name) ||
			!conversationProperties.name ||
			!conversationProperties.name.length ||
			conversationProperties.name.length > EntityLengths.conversation.name.max
		) {
			showWarning(fmtMsg(ConversationBuilderLocale.ConversationNameInvalidMsg));
			return false;
		}

		// check the nodes validations
		if (Object.keys(nodes).length) {
			// there must be a start node
			if (!Object.keys(nodes).find((f) => nodes[f].type === ConversationNodeType.Conversation)) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversatoinStartNodeRequired));
				return false;
			}
			// any path should not be connected to any other path
			const connectors = Object.keys(nodes)
				.filter((f) => nodes[f].type === ConversationNodeType.Connector)
				.map((m) => nodes[m] as ConversationNode<Connector>);

			// every connector node should have 'source' and 'target'
			if (connectors.find((f: any & ConversationNode<Connector>) => !f.data.sourceId || !f.data.targetId)) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationRemoveOrphanConnectorsMsg));
				return false;
			}

			// every dialog and path should have required properties
			let isPassed = Object.keys(nodes).every((n) => {
				if (
					nodes[n].type === ConversationNodeType.Conversation ||
					nodes[n].type === ConversationNodeType.Dialog
				) {
					const node = (nodes[n] as any) as ConversationNode<Dialog>;
					const lengthProp = EntityLengths.dialog;
					if (
						!node.data.name ||
						node.data.name.trim().length < lengthProp.name.min ||
						node.data.name.trim().length > lengthProp.name.max
					) {
						showWarning(
							fmtMsg(ConversationBuilderLocale.ConversationDialogNameInValidMsg, { 0: node.data.name })
						);
						return false;
					}
					if (
						!node.data.text ||
						node.data.text.trim().length == 0 ||
						node.data.text.trim().length > lengthProp.description.max
					) {
						showWarning(
							fmtMsg(ConversationBuilderLocale.ConversationDialogTxtInValidMsg, { 0: node.data.name })
						);
						return false;
					}
					if (
						node.data.retryCount < lengthProp.retries.min ||
						node.data.retryCount > lengthProp.retries.max
					) {
						showWarning(
							fmtMsg(ConversationBuilderLocale.ConversationDialogRetryInValidMsg, { 0: node.data.name })
						);
						return false;
					}
					if (node.type == ConversationNodeType.Dialog && !node.data.interactionStyleId) {
						showWarning(fmtMsg(ConversationBuilderLocale.ConversationDialogInteractionStyleInvalidMsg));
						return false;
					}
					if (
						node.type == ConversationNodeType.Conversation &&
						typeof node.data.isHead === "boolean" &&
						node.data.isHead &&
						(!node.data.interactionStyleId ||
							![InteractionStyles.Neutral].includes(node.data.interactionStyleId))
					) {
						showWarning(
							fmtMsg(ConversationBuilderLocale.ConversationDialogStartInteractionStyleInvalidMsg)
						);
						return false;
					}
				} else if (nodes[n].type === ConversationNodeType.Path) {
					const node = (nodes[n] as any) as ConversationNode<Path>;
					const lengthProp = EntityLengths.path;
					if (
						!node.data.name ||
						node.data.name.trim().length < lengthProp.name.min ||
						node.data.name.trim().length > lengthProp.name.max
					) {
						showWarning(
							fmtMsg(ConversationBuilderLocale.ConversationPathNameInValidMsg, { 0: node.data.name })
						);
						return false;
					}

					// There should be only one connector targetting a path.
					// if (connectors.filter((c) => c.data.targetId === node.id).length > 1) {
					// 	showWarning(
					// 		fmtMsg(ConversationBuilderLocale.ConversationPathTooManyConnections, { 0: node.data.name })
					// 	);
					// 	return false;
					// }

					if (
						!node.data.interactionStyleId ||
						![
							InteractionStyles.Positive,
							InteractionStyles.Negative,
							InteractionStyles.Encouraging
						].includes(node.data.interactionStyleId)
					) {
						showWarning(fmtMsg(ConversationBuilderLocale.ConversationPathInteractionStyleInvalidMsg));
						return false;
					}

					// A regular path must have at least one intent.
					if (!node.data.isFallback && (!node.data.intents || !node.data.intents.length)) {
						showWarning(
							fmtMsg(ConversationBuilderLocale.ConversationPathIntentRequired, { 0: node.data.name })
						);
						return false;
					}

					if (node.data.intents && node.data.intents.length) {
						const pathIntentIds = node.data.intents.map((i) => i.id);
						const pathIntents = Object.keys(intents.directory)
							.filter((key) => pathIntentIds.includes(key))
							.map((key) => intents.directory[key]);
						const pathIntentEntities = pathIntents
							.filter((pi) => pi.entityId)
							.map((pi) => entities.directory[pi.entityId!])
							.filter((pie) => !isUndefined(pie) && !isNull(pie));

						// If the intents used by the path uses some entities,
						// then, at least 1 entity value must be selected from each entity in the path.
						if (pathIntentEntities && pathIntentEntities.length) {
							const isEntityValueUsed = pathIntentEntities.every((pie) => {
								const pathEntityValueIds = pie.values.map((v) => v.id);

								if (node.data.entityValueIds.some((evId) => pathEntityValueIds.includes(evId))) {
									return true;
								} else {
									showWarning(
										fmtMsg(ConversationBuilderLocale.ConversationPathEntityValueRequired, {
											0: pie.name,
											1: node.data.name
										})
									);
									return false;
								}
							});

							if (!isEntityValueUsed) {
								return false;
							}
						}
					}
				}
				return true;
			});

			if (!isPassed) {
				return false;
			}

			const isAnyDialogHasConclusion = Object.keys(nodes)
				.filter((n) => nodes[n].type === ConversationNodeType.Dialog)
				.some((n) => {
					const node = (nodes[n] as any) as ConversationNode<Dialog>;
					if (typeof node.data.isConclusion === "boolean" && node.data.isConclusion) {
						return true;
					} else {
						return false;
					}
				});

			if (!isAnyDialogHasConclusion) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationDialogConclusionRequiredMsg));
				return false;
			}

			// validate entity names and lengths.
			isPassed = Object.keys(entities.directory).every((n) => {
				const entity = entities.directory[n];
				const lengthProp = EntityLengths.entity;
				if (
					!entity.name ||
					entity.name.trim().length < lengthProp.name.min ||
					entity.name.trim().length > lengthProp.name.max ||
					entity.values.some(
						(v) =>
							v.value.trim().toLocaleLowerCase().length > lengthProp.values.maxLength ||
							v.value.trim().toLowerCase().length < lengthProp.values.minLength
					)
				) {
					showWarning(fmtMsg(ConversationBuilderLocale.ConversationEntityInValidMsg, { 0: entity.name }));
					return false;
				}
				if (invalidIntentNameRegex.test(entity.name)) {
					showWarning(fmtMsg(ConversationBuilderLocale.ConversationEntityInvalidChar, { 0: entity.name }));
					return false;
				}

				return true;
			});

			//validate count of values per entity and count of values per conversation
			let totalNumberOfSynonymsPerConversation = 0;

			Object.keys(entities.directory).forEach((n) => {
				const currentEntitySynonymsCount = entities.directory[n].values.length;
				totalNumberOfSynonymsPerConversation += currentEntitySynonymsCount;

				if (currentEntitySynonymsCount > CLUValidations.entitySynonymsMaxCount) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationEntitySynonymsMaxCountReachedMsg, {
							value: entities.directory[n].name
						})
					);
					return false;
				}
			});

			if (totalNumberOfSynonymsPerConversation > CLUValidations.projectSynonymsMaxCount) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationProjectSynonymsMaxCountReachedMsg));
				return false;
			}

			if (!isPassed) {
				return false;
			}

			isPassed = Object.keys(intents.directory).every((n) => {
				const intent = intents.directory[n];
				const lengthProp = EntityLengths.intent;

				if (!intent.name) {
					showWarning(fmtMsg(ConversationBuilderLocale.ConversastionIntentNameRequired));
					return false;
				}
				if (
					intent.name.trim().length < lengthProp.name.min ||
					intent.name.trim().length > lengthProp.name.max
				) {
					showWarning(fmtMsg(ConversationBuilderLocale.ConversationIntentNameInValidMsg, { 0: intent.name }));
					return false;
				}
				if (intent.utterances.some((u) => u.value.length > lengthProp.utterance.maxLength)) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentUtteranceInvalidLength, { 0: intent.name })
					);

					return false;
				}
				if (intent.utterances.length > lengthProp.utterance.max) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentUtteranceCountInvalid, { 0: intent.name })
					);

					return false;
				}
				if (intent.utterances.length === 0) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentUtteranceRequired, { 0: intent.name })
					);

					return false;
				}
				if (invalidIntentNameRegex.test(intent.name)) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentNameInvalidCharMsg, { 0: intent.name })
					);
					return false;
				}

				if (intent.utterances.some((val) => !isUtterenceValueValid(val.value))) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentUtteranceInvalidCharMsg, { 0: intent.name })
					);
					return false;
				}

				const { isUnique, isValidEntity, invalidEntityName } = validateUtterances(
					intent,
					intents.directory,
					entities.directory
				);

				if (!isUnique) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentUtteranceUniqueMsg, { 0: intent.name })
					);
					return false;
				}

				if (!isValidEntity) {
					showWarning(
						fmtMsg(ConversationBuilderLocale.ConversationIntentUtteranceInvalidEntityMsg, {
							0: intent.name,
							1: invalidEntityName
						})
					);
					return false;
				}

				return true;
			});

			if (!isPassed) {
				return false;
			}

			const allEntities = Object.values(entities.directory);
			const allIntents = Object.values(intents.directory);

			if (!this.areNamesValid(allEntities, allIntents)) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationEntityIntentUnique));
				return false;
			}

			const pathIntentNodes = Object.keys(nodes).filter((n) => nodes[n].type === ConversationNodeType.Path);
			const entityValueIds: string[] = pathIntentNodes.flatMap((id) => (nodes[id] as any).data.entityValueIds);

			const allPaths = Object.entries(nodes)
				.filter((m) => m[1].type === ConversationNodeType.Path)
				.map((m) => m[1]);

			const duplicatePathIntentUtterances: PathIntentDuplicateUtterances[] = await new Promise((res, rej) => {
				this.duplicatedValidationResolveCallback = res;
				this.duplicatedValidationRejectCallback = rej;

				(this.validateNodesWorker as Worker).postMessage({ allPaths, allIntents, allEntities, entityValueIds });
			});

			const displayWarningMessage = (h: any) => {
				return h("div", { style: { maxHeight: "400px", overflow: "auto", padding: "10px" } }, [
					duplicatePathIntentUtterances.length &&
						duplicatePathIntentUtterances.map((piu) => {
							return h("div", null, [
								h(
									"span",
									null,
									`${fmtMsg(ConversationBuilderLocale.ConversationDuplicateUtteranceWarningText, {
										0: piu.intents.join(", "),
										1: piu.paths.join(", ")
									})}`
								),
								h("div", null, [
									h("ul", null, [
										h(
											"li",
											null,
											piu.utterances.map((u) => h("li", null, u))
										)
									])
								]),
								h("br")
							]);
						})
				]);
			};

			if (duplicatePathIntentUtterances.length) {
				Modal.warning({
					title: fmtMsg(ConversationBuilderLocale.ConversationUtterenceUnique),
					content: displayWarningMessage,
					onOk: () => {
						return false;
					}
				});
				return false;
			}

			//validate number of intents, utterances, entities per conversation.
			if (hasMaxLimitOfUtterancesExceeded(allIntents, allEntities, entityValueIds)) {
				showWarning(
					fmtMsg(ConversationBuilderLocale.ConversationUtterancesCountValidationMsg, {
						0: CLUValidations.allUtterancesMaxCount
					})
				);
				return false;
			}

			if (
				allIntents.length &&
				(allIntents.length > CLUValidations.allIntentsMaxCount ||
					allIntents.length < CLUValidations.allIntentsMinCount)
			) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationIntentCountValidationMsg));
				return false;
			}
			if (allEntities.length > CLUValidations.allEntitiesMaxCount) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationEntityCountValidationMsg));
				return false;
			}

			// every dialog and path should be in source or target of connector
			const dialogAndPaths = Object.entries(nodes)
				.filter((m) => m[1].type !== ConversationNodeType.Connector)
				.map((m) => m[1]);

			// traverse nodes
			if (Object.entries(nodes).length > 1) {
				const nodesTraversed: string[] = []; // except connectors
				const start = Object.entries(nodes).find((f) => f[1].type === ConversationNodeType.Conversation);
				let nextNodesToTraverse: string[] = [];
				if (start) {
					nodesTraversed.push(start[1].id);
					const newNodes = connectors
						.filter((c) => c.data.sourceId === start[1].id) // connector should have start as source
						.map((m) => m.data.targetId) // get all targetIds
						.filter((f) => f && !nodesTraversed.some((s) => s === f)); // target should be defined and should not be in already traversed list
					nextNodesToTraverse.push(...(newNodes as any));
				}

				while (nextNodesToTraverse.length) {
					// find connector with source in next nodes
					const newNodes = connectors
						.filter((f) => nextNodesToTraverse.some((s) => s === f.data.sourceId)) // find all the connectors with source in 'nextNodesToTraverse'
						.map((m) => m.data.targetId) // get all targetIds
						.filter((f) => f && !nodesTraversed.some((s) => s === f)); // target should be defined and should not be in already traversed list

					nodesTraversed.push(...nextNodesToTraverse);

					nextNodesToTraverse = newNodes as string[];
				}

				if (dialogAndPaths.length !== uniq(nodesTraversed).length) {
					showWarning(fmtMsg(ConversationBuilderLocale.ConversationRemoveUnconnectedConnectorMsg));
					return false;
				}
			}

			// validation from backend
			if (checkNameUniqueness && !(await this.conversationStore.isNameUnique(conversationProperties.name))) {
				showWarning(fmtMsg(ConversationBuilderLocale.ConversationAllreadyExits));
				return false;
			}
		} else {
			showWarning(fmtMsg(ConversationBuilderLocale.ConversationAddNodesMsg));
			return false;
		}
		return true;
	}

	private areNamesValid(allEntities: Entity[], allIntents: Intent[]): boolean {
		const allIntentAndEntityNames = allEntities
			.map((e) => e.name.trim().toLowerCase())
			.concat(allIntents.map((i) => i.name.trim().toLowerCase()));

		return uniq(allIntentAndEntityNames).length === allIntentAndEntityNames.length;
	}

	get locale() {
		return ConversationBuilderLocale;
	}

	get changeCount(): number {
		return this.conversationStore.changes ? this.conversationStore.changes.length : 0;
	}

	private purifyData() {
		// Check if entities are used in intents and intents are used in path.
		const { entities, intents, nodes } = this.conversationStore;

		const paths = Object.keys(nodes)
			.filter((key) => nodes[key].type === ConversationNodeType.Path)
			.map((key) => nodes[key] as ConversationNode<Path>);

		const intentsArr = Object.values(intents.directory);
		const entitiesArr = Object.values(entities.directory);

		// Unused intents = intents which is not used in any path.
		const unusedIntents = intentsArr.filter(
			(intent) => !paths.some((p) => p.data.intents.some((i) => i.id === intent.id))
		);

		const usedIntents = differenceBy(intentsArr, unusedIntents, "id");

		// Unused entities = entities which is not used in any used intent.
		const unusedEntities = entitiesArr.filter((entity) => !usedIntents.some((i) => i.entityId === entity.id));

		const isSaveDetail = unusedIntents.length || unusedEntities.length;

		unusedIntents.forEach((intent) => this.conversationStore.deleteIntent(intent.id));
		unusedEntities.forEach((entity) => this.conversationStore.deleteEntity(entity.id));

		// This would save the json detail first before publishing.
		if (isSaveDetail) {
			this.conversationStore.setChangedSinceLastSave(true);
		}
	}
}
